import { InflateRequestMode } from '@sccs/types';
import { RequestHeaderConfiguration } from '../../../types/ApiClientConfig';
import { OptionalProductDto, OptionalProductDtoListWithId } from '../../../types/dls/dls-response/ProductDto';
import { SharedRequestConfiguration, SharedRequestDlsParameter } from '../../../types/GeneralParameter';
import { convertFieldsToStrings } from '../../../util/ConvertPropertiesToString';
import { createFilterCriteriaString, getVansFilterCriteria } from '../../../util/dls/HandleFilterCriteria';
import { getRequestConfiguration } from '../../../util/dls/RequestConfiguration';
import { isVanByMode } from '../../../util/VansConfiguration';
import { GenericFetchClient, ModeSpecificFetchClient } from '../../GenericFetchClient';

/**
 * Available query parameters at dls endpoint /teaser only
 */
export type DlsTeaser = SharedRequestDlsParameter & {
  filterCriteria: UnifiedTeaserFilterCriteria;
};

export type FilterCriteriaDlsTeaser = {
  modelIdentifier?: string;
  brand: string[];
};

export type FilterCriteriaPi = FilterCriteriaDlsTeaser & {
  baumuster4?: string;
};

export type FilterCriteriaCampaign = FilterCriteriaDlsTeaser & {
  bodyType?: string;
  campaign: string;
};

export type FilterCriteriaHome = FilterCriteriaDlsTeaser;

export type UnifiedTeaserFilterCriteria = FilterCriteriaPi | FilterCriteriaHome | FilterCriteriaCampaign;

export class DlsTeaserClient extends GenericFetchClient implements ModeSpecificFetchClient {
  constructor(config: RequestHeaderConfiguration) {
    super(config);
    this.setPathSegment('/search/teaser');
  }
  getInflateIdLength(): number {
    throw new Error('Method not implemented.');
  }
  setMode(): void {
    throw new Error('Method not implemented.');
  }

  setRequiredParameters({ filterCriteria, ...rest }: DlsTeaser, configuration: SharedRequestConfiguration): void {
    const additionalRequestConfig = this.getAdditionalRequestConfig(configuration, { filterCriteria, ...rest });

    this.setQueryParameters({
      ...convertFieldsToStrings(rest),
      clusteringCriteria: additionalRequestConfig.clusteringCriteria,
      ctx: additionalRequestConfig.ctx,
      vehicleAssortment: additionalRequestConfig.vehicleAssortment,
      filterCriteria: additionalRequestConfig.filters,
    });
  }

  private getAdditionalRequestConfig(
    configuration: SharedRequestConfiguration,
    { filterCriteria, ...rest }: DlsTeaser,
  ) {
    const isVan = isVanByMode(configuration.mode);
    const filter = this.getFilterByMode(configuration.mode, filterCriteria);
    const { clusteringCriteria, vehicleAssortment, ctx } = getRequestConfiguration(
      isVan,
      {
        ...rest,
        filterCriteria: filter,
      },
      configuration.mode,
    );

    return {
      clusteringCriteria: clusteringCriteria?.join(',') ?? '',
      ctx: ctx ?? '',
      vehicleAssortment: rest.vehicleAssortment ?? vehicleAssortment ?? '',
      filters: this.getUpdatedFilterCriteria(isVan, filter),
    };
  }

  private getFilterByMode(
    mode: InflateRequestMode,
    filterCriteria: UnifiedTeaserFilterCriteria,
  ): UnifiedTeaserFilterCriteria {
    switch (mode) {
      case InflateRequestMode.PI_PAGES_CARS:
      case InflateRequestMode.PI_PAGES_VANS:
        return filterCriteria as FilterCriteriaPi;

      case InflateRequestMode.HOMEPAGE_CARS:
      case InflateRequestMode.HOMEPAGE_VANS:
        return filterCriteria as FilterCriteriaHome;

      case InflateRequestMode.CAMPAIGN_TEASER_CARS:
      case InflateRequestMode.CAMPAIGN_TEASER_VANS:
        return filterCriteria as FilterCriteriaCampaign;

      default:
        throw Error('Unknown Teaser Mode');
        return filterCriteria;
    }
  }

  private getUpdatedFilterCriteria(isVan: boolean, filter: UnifiedTeaserFilterCriteria): string {
    if (isVan) {
      return getVansFilterCriteria(filter);
    }
    return createFilterCriteriaString(filter);
  }

  checkAllRequiredParametersPresent(): boolean {
    throw new Error('Method not implemented.');
  }

  async getResponse(): Promise<OptionalProductDtoListWithId> {
    const productDtoList = await this.fetchTypedBody<OptionalProductDto[]>();
    return {
      responseTypeId: 'ProductDtoList',
      productDtoList: productDtoList,
    };
  }
}
